import type { ReactNode } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatMonthYear } from '../../i18n/format';
import { IconButton } from './controls';
import { Wordmark } from './text';

/** Top of Home: the wordmark slot (the final product name drops in here) and optional actions. */
export function AppHeader({ actions }: { actions?: ReactNode }) {
  return (
    <header className="app-header">
      <Wordmark />
      {actions}
    </header>
  );
}

export interface YearMonth {
  year: number;
  month: number; // 1-12
}

export function addMonths({ year, month }: YearMonth, delta: number): YearMonth {
  const i = year * 12 + (month - 1) + delta;
  return { year: Math.floor(i / 12), month: (i % 12) + 1 };
}

/**
 * The month is the page's station sign. Chevrons step months; the label
 * is a live region so screen readers hear the new month. `max` disables
 * stepping into the future.
 */
export function MonthSwitcher({
  value,
  onChange,
  language,
  min,
  max,
  level = 1,
}: {
  value: YearMonth;
  onChange: (next: YearMonth) => void;
  language: string;
  min?: YearMonth;
  max?: YearMonth;
  /** The month is the page's heading on Home (h1); under a screen title it is an h2. */
  level?: 1 | 2;
}) {
  const { t } = useTranslation();
  const index = (m: YearMonth) => m.year * 12 + m.month;
  const atMax = max ? index(value) >= index(max) : false;
  const atMin = min ? index(value) <= index(min) : false;
  const Heading = level === 1 ? 'h1' : 'h2';
  return (
    <div className="month-switcher">
      <IconButton icon={ChevronLeft} label={t('common.previousMonth')} disabled={atMin} onClick={() => onChange(addMonths(value, -1))} />
      <Heading className="month-switcher-label" aria-live="polite">
        {formatMonthYear(value.year, value.month, language)}
      </Heading>
      <IconButton icon={ChevronRight} label={t('common.nextMonth')} disabled={atMax} onClick={() => onChange(addMonths(value, 1))} />
    </div>
  );
}
